import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore } from "@/store/profile";
import {
  useHealthHistoryStore,
  MEDICINE_QUESTIONS,
  MEDICINE2_QUESTIONS,
} from "@/store/healthHistory";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";

// 44px tall, 16px text so iOS Safari doesn't zoom on focus.
const selectClass =
  "block min-h-11 w-full rounded-lg border border-[#e2e2ec] bg-white px-3 py-2 text-base font-medium text-[#1E252B] focus:outline-none focus:ring-2 focus:ring-[#2A4064]/30 focus:border-[#2A4064]";

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

export function HealthHistory2Page() {
  const navigate = useNavigate();
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const isFemale = (currentPatient?.sex ?? "").toUpperCase() === "F";

  const medicineAnswers = useHealthHistoryStore((s) => s.medicineAnswers);
  const setMedicineAnswers = useHealthHistoryStore((s) => s.setMedicineAnswers);
  const medicine2Answers = useHealthHistoryStore((s) => s.medicine2Answers);
  const setMedicine2Answers = useHealthHistoryStore((s) => s.setMedicine2Answers);

  const yearOptions = useMemo(() => {
    const dobYear = currentPatient?.dob ? new Date(currentPatient.dob).getFullYear() : 1950;
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = dobYear; y <= currentYear; y++) years.push(y);
    return years.reverse();
  }, [currentPatient?.dob]);

  const medicineQuestionsWithOptional = useMemo(() => {
    const list = [...MEDICINE_QUESTIONS];
    if (isFemale) list.push({ code: "BirthControl", question: "Birth control" });
    return list;
  }, [isFemale]);

  const medicine2WithOptional = useMemo(() => {
    const list = [...MEDICINE2_QUESTIONS];
    if (isFemale) {
      list.push({ code: "Pregnant", question: "Are you pregnant?", isDate: false });
      list.push({ code: "LastMenstrualPeriod", question: "Date of last menstrual period?", isDate: true });
    }
    return list;
  }, [isFemale]);

  const goBack = () => navigate("/complaint/health-history");
  const goNext = () => navigate("/complaint/health-history-3");

  if (!currentPatient) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content px-4 pt-4 pb-0">
          <p className="text-sm text-slate-600">Please sign in or select a patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content px-4 pt-4 pb-0">
        <div className="complaint-history-card">
          <div className="complaint-history-heading">
            <div className="complaint-history-heading-bar" />
            <h2 className="complaint-history-heading-text">Have you ever taken or experienced any of the following</h2>
          </div>
          <div className="complaint-history-divider" />

          <div className="complaint-history-section-block history-choice-rows grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {medicineQuestionsWithOptional.map((m) => {
              const answer = medicineAnswers[m.code]?.answer ?? "";
              const yearId = `med-${m.code}-year`;
              return (
                <div key={m.code} className="complaint-history-med-card history-choice-row">
                  <ChoiceGroup
                    id={`med-${m.code}`}
                    layout="segmented"
                    clearable
                    legend={m.question}
                    options={YES_NO_OPTIONS}
                    value={answer || null}
                    onChange={(v) =>
                      setMedicineAnswers((prev) => ({
                        ...prev,
                        [m.code]: { ...(prev[m.code] ?? { answer: "", year: "" }), answer: v ?? "" },
                      }))
                    }
                  />
                  {answer === "Yes" && (
                    <div className="mt-3">
                      <label htmlFor={yearId} className="mb-1 block text-[15px] font-semibold text-[#1E252B]">
                        What year?
                      </label>
                      <select
                        id={yearId}
                        className={selectClass}
                        value={medicineAnswers[m.code]?.year ?? ""}
                        onChange={(e) =>
                          setMedicineAnswers((prev) => ({
                            ...prev,
                            [m.code]: { ...(prev[m.code] ?? { answer: "", year: "" }), year: e.target.value },
                          }))
                        }
                      >
                        <option value="">Select year</option>
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="complaint-history-divider my-6" />
          <div className="complaint-history-section-block history-choice-rows">
            {medicine2WithOptional.map((m) =>
              m.isDate ? (
                <div key={m.code} className="history-choice-row mb-6">
                  <label htmlFor={`med2-${m.code}`} className="choice-textarea-label">
                    {m.question}
                    {m.code === "LastMenstrualPeriod" && (
                      <span className="choice-group__hint"> Leave empty if not sure.</span>
                    )}
                  </label>
                  <input
                    id={`med2-${m.code}`}
                    type="date"
                    className={`${selectClass} sm:max-w-xs`}
                    value={medicine2Answers[m.code] ?? ""}
                    onChange={(e) => setMedicine2Answers((prev) => ({ ...prev, [m.code]: e.target.value }))}
                  />
                </div>
              ) : (
                <ChoiceGroup
                  key={m.code}
                  id={`med2-${m.code}`}
                  layout="segmented"
                  clearable
                  legend={m.question}
                  options={YES_NO_OPTIONS}
                  value={medicine2Answers[m.code] || null}
                  onChange={(v) => setMedicine2Answers((prev) => ({ ...prev, [m.code]: v ?? "" }))}
                  className="history-choice-row sm:max-w-xs"
                />
              )
            )}
          </div>
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>
          {t.backButton}
        </Button>
        <Button type="button" onClick={goNext}>
          {t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
